/**
 * useBlockContact — blocks the sender of an inbox email, mirroring the classic
 * SmartInbox "Add to Blacklist" / "Block Contact" behaviour exactly.
 *
 * Two block levels, decided automatically from the sender domain:
 *   - Custom domains (e.g. @acme.com)   → DOMAIN-level block. A single blacklist
 *     doc `@acme.com` is written and EVERY contact in `World/{uid}/db` whose
 *     lead_email ends with that domain is marked Not Qualified + globalOptOut.
 *   - Consumer providers (gmail, gmx …) → EMAIL-level block only (we must never
 *     blacklist a whole shared provider). Only the matching contact is updated.
 *
 * The work runs in the `blockInboxSender` Cloud Function (europe-west1). It used
 * to run here, which meant a domain block had to `getDocs` the ENTIRE contact
 * collection into the browser just to find the addresses ending in that domain —
 * Firestore has no suffix operator, so there was no client-side alternative. On
 * a world with tens of thousands of contacts that is many megabytes downloaded
 * per click. The function does the identical scan next to the data and answers
 * with a small summary.
 *
 * The blacklist is then enforced server-side by WorldEmailSender / outreach /
 * flow automation, so blocked senders can no longer be emailed.
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebaseConfig';
import { getBlockTarget } from '../utils/blockTarget';

// Re-exported because InboxView needs it for the confirmation dialog, and this
// hook has been its import path since before it was extracted.
export { getBlockTarget };

export default function useBlockContact(userId) {
  const [blocking, setBlocking] = useState(false);

  /**
   * Blocks the sender of the given email.
   *
   * @param {object} email an inbox email doc (uses `email.from`)
   * @returns {Promise<{ success: boolean, message: string,
   *                     blockType?: string, blockEntry?: string }>}
   */
  const blockContact = useCallback(async (email) => {
    if (blocking || !email || !userId) {
      return { success: false, message: 'Nothing to block.' };
    }

    // Validated here too so an unparseable sender never costs a round trip and
    // the message matches what the confirmation dialog showed.
    const target = getBlockTarget(email.from);
    if (!target) {
      return { success: false, message: 'No valid sender address found in this email.' };
    }

    setBlocking(true);
    try {
      const blockSender = httpsCallable(functions, 'blockInboxSender');
      const { data } = await blockSender({ userId, from: email.from });
      return {
        success: true,
        message: data.message,
        blockType: data.blockType,
        blockEntry: data.blockEntry,
      };
    } catch (err) {
      console.error('[useBlockContact] block failed:', err);
      return { success: false, message: err?.message || 'Could not block contact.' };
    } finally {
      setBlocking(false);
    }
  }, [blocking, userId]);

  return { blocking, blockContact };
}
